// Returning-learner entry resolution (presentation routing only).
//
// Decides where the DEFAULT app entry (`/app`) should take a signed-in person
// who is enrolled in a Course Lab cohort but has no Workitect workspace. It
// confers nothing: the memberships come from an RLS-scoped read of the
// caller's own rows, and every destination is itself protected by AuthGuard,
// RLS and the cohort RPCs. It only prevents a cohort-only learner from being
// diverted into general workspace onboarding.

export interface LearnerMembershipRow {
  cohortId: string;
  state: string;
}

export type LearnerEntry =
  | { kind: 'ordinary' }
  | { kind: 'course'; cohortId: string }
  | { kind: 'choose'; cohortIds: string[] };

/**
 * Enrolled states that route a returning learner to their course. Mirrors the
 * production `course_cohort_can_view` learner branch and the enrolled count
 * (`active`, `inactive`, `completed`). `invited` is not yet enrolled;
 * withdrawn/removed/archived never route (and cannot read the cohort anyway).
 */
export const ROUTABLE_MEMBER_STATES: readonly string[] = ['active', 'inactive', 'completed'];
/** Learner states the production `course_cohort_can_view` grants visibility to. */
export const VISIBLE_MEMBER_STATES: readonly string[] = ['invited', 'active', 'inactive', 'completed'];

/** Navigation state asking the default entry to list courses instead of auto-routing. */
export const COURSE_LIST_STATE = { courseEntry: 'list' } as const;

export function resolveLearnerEntry(input: {
  isArchitect: boolean;
  hasWorkspace: boolean;
  memberships: LearnerMembershipRow[];
}): LearnerEntry {
  // Architects and workspace members keep ordinary navigation untouched.
  if (input.isArchitect || input.hasWorkspace) return { kind: 'ordinary' };
  const ids = Array.from(
    new Set(
      input.memberships
        .filter((m) => m.cohortId && ROUTABLE_MEMBER_STATES.includes(m.state))
        .map((m) => m.cohortId),
    ),
  );
  if (ids.length === 0) return { kind: 'ordinary' };
  if (ids.length === 1) return { kind: 'course', cohortId: ids[0] };
  return { kind: 'choose', cohortIds: ids };
}

export const courseSpacePath = (cohortId: string) =>
  `/app/course-lab/${encodeURIComponent(cohortId)}`;

/**
 * Same-origin return path for a signed-out visitor to a protected course
 * surface. Only the path and a UUID-shaped `cohort` query survive; any other
 * query or fragment is dropped so nothing private can ride along.
 */
const UUIDISH = /^[0-9a-f-]{8,64}$/i;
/** Preserve exact selected run between module links; never chooses authority. */
export function courseModulePath(path: string, search: string): string {
  const modulePath = /^\/app\/lab\/ai-in-project-management(?:\/m[234])?$/;
  if (!modulePath.test(path)) return path;
  const params = new URLSearchParams(search), scoped = new URLSearchParams();
  for (const key of ['cohort', 'run']) { const value = params.get(key); if (value && UUIDISH.test(value)) scoped.set(key, value); }
  const query=scoped.toString();
  return path + (query ? `?${query}` : '');
}
export function courseReturnPath(pathname: string, search: string): string | null {
  if (!pathname.startsWith('/app/')) return null;
  let suffix = '';
  try {
    const c = new URLSearchParams(search).get('cohort');
    if (c && UUIDISH.test(c)) { suffix = `?cohort=${c}`; const r = new URLSearchParams(search).get('run'); if(r && UUIDISH.test(r)) suffix += `&run=${r}`; }
  } catch {
    /* ignore malformed query */
  }
  return pathname + suffix;
}

/**
 * Destination after a SUCCESSFUL module save & exit: the module's own course
 * (UUID-shaped `cohort` query only; the hub re-checks access), else the
 * neutral default entry. Never the unavailable-course list state, which is
 * reserved for a course page that genuinely could not open.
 */
export function saveExitPath(search: string): string {
  try {
    const c = new URLSearchParams(search).get('cohort');
    if (c && UUIDISH.test(c)) return courseSpacePath(c);
  } catch {
    /* ignore malformed query */
  }
  return '/app';
}
